import { useCallback, useEffect, useState } from "react";
import Aos from "aos";
import "aos/dist/aos.css";
import "../../Components/SPSSRegistration/index.css";
import useSPSSRegistrationForm, {
  SECTION_DEFS,
  SECTION_KEYS,
} from "../../Components/SPSSRegistration/useSPSSRegistrationForm";
import ProgressBar from "../../Components/SPSSRegistration/ProgressBar";
import QuestionCard from "../../Components/SPSSRegistration/QuestionCard";
import ConfirmationDialog from "../../Components/SPSSRegistration/ConfirmationDialog";
import ReviewModal from "../../Components/SPSSRegistration/ReviewModal";
import SectionCard from "../../Components/SPSSRegistration/SectionCard";
import SuccessScreen from "../../Components/SPSSRegistration/SuccessScreen";
import spssQuestionsConfig from "../../Components/SPSSRegistration/spssQuestionsConfig";

export default function SPSSRegistration() {
  const {
    formData,
    errors,
    activeId,
    activeSection,
    currentQuestion,
    visibleQuestions,
    revealedIds,
    sections,
    progress,
    submitted,
    submitting,
    completedSections,
    isLastInSection,
    sectionStatus,
    primaryContact,
    reviewSections,
    setFieldValue,
    goNext,
    goBack,
    goToQuestion,
    resetProgressFrom,
    enterSection,
    leaveSection,
    saveAndBack,
    validateForSubmit,
    confirmSubmit,
    resetForm,
  } = useSPSSRegistrationForm();

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    questionId: null,
  });
  const [resetDialog, setResetDialog] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const openReview = useCallback(() => {
    if (validateForSubmit()) {
      setReviewOpen(true);
    }
  }, [validateForSubmit]);

  const handleConfirmSubmit = useCallback(async () => {
    const ok = await confirmSubmit();
    if (ok) {
      setReviewOpen(false);
    }
  }, [confirmSubmit]);

  useEffect(() => {
    document.title = "Pendaftaran SPSS 2026 - HIMSTAT";
    Aos.init();
    window.scrollTo({ top: 0 });
  }, []);

  const affectsBranching = useCallback((questionId) => {
    const q = spssQuestionsConfig.find((c) => c.id === questionId);
    return q && q.affectsBranching;
  }, []);

  const handleEdit = useCallback(
    (questionId) => {
      if (affectsBranching(questionId)) {
        setConfirmDialog({ open: true, questionId });
      } else {
        goToQuestion(questionId);
      }
    },
    [affectsBranching, goToQuestion]
  );

  const handleConfirmEdit = useCallback(() => {
    const { questionId } = confirmDialog;
    setConfirmDialog({ open: false, questionId: null });
    if (questionId) {
      resetProgressFrom(questionId);
    }
  }, [confirmDialog, resetProgressFrom]);

  const handleCancelEdit = useCallback(() => {
    setConfirmDialog({ open: false, questionId: null });
  }, []);

  if (submitted) {
    return <SuccessScreen />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Decorative background glow */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-circle opacity-40" />
      </div>

      {/* Header */}
      <div className="px-6 pt-20 pb-8 desktop:px-0 desktop:pt-24 desktop:pb-10 text-center">
        <h1
          data-aos="fade-up"
          className="text-3xl desktop:text-5xl large-desktop:text-6xl font-bold text-white mb-3"
        >
          Pendaftaran Lomba{" "}
          <span className="text-transparent font-bold bg-clip-text bg-gradient-to-r from-white via-white/80 to-white/50">
            SPSS 2026
          </span>
        </h1>
        <p className="text-white/40 font-sans text-sm desktop:text-base max-w-xl mx-auto leading-relaxed">
          Statistics Student Summit — HIMSTAT Binus University
        </p>
      </div>

      {activeSection ? (
        /* ── Active section (incremental form) ─────────────────────────────── */
        <>
          {/* Back to section selector */}
          <div className="px-6 desktop:px-0 max-w-2xl mx-auto w-full mb-4">
            <button
              type="button"
              onClick={leaveSection}
              className="flex items-center gap-2 text-white/30 hover:text-white/60
                transition-colors font-sans text-sm cursor-pointer"
            >
              <i className="fa-solid fa-arrow-left text-xs" />
              Kembali ke Daftar Bagian
            </button>
          </div>

          {/* Progress */}
          <div className="px-6 desktop:px-0 max-w-2xl mx-auto w-full mb-8">
            <ProgressBar
              progress={progress}
              currentQuestion={currentQuestion}
              sections={sections}
            />
          </div>

          {/* Form body */}
          <div className="flex-1 px-4 desktop:px-0 max-w-2xl mx-auto w-full pb-20">
            <div className="flex flex-col gap-5">
              {visibleQuestions.map((q, idx) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  value={formData[q.id]}
                  onChange={setFieldValue}
                  error={errors[q.id]}
                  isActive={q.id === activeId}
                  isRevealed={revealedIds.has(q.id)}
                  questionNumber={idx + 1}
                  onBack={goBack}
                  onNext={goNext}
                  onSubmit={saveAndBack}
                  onEdit={handleEdit}
                  isFirst={idx === 0}
                  isLast={isLastInSection}
                  submitLabel="Simpan & Kembali"
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        /* ── Section selector ──────────────────────────────────────────────── */
        <div className="flex-1 px-6 desktop:px-0 max-w-2xl mx-auto w-full pb-20">
          <div className="flex flex-col gap-5">
            {SECTION_KEYS.map((key) => (
              <SectionCard
                key={key}
                section={SECTION_DEFS[key]}
                status={sectionStatus(key)}
                onClick={() => enterSection(key)}
              />
            ))}
          </div>

          <div className="flex justify-end mt-6">
            <button
              type="button"
              onClick={() => setResetDialog(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-red/30 text-[#EC1B23]
                hover:bg-red/10 hover:border-red/50 transition-all duration-300
                font-sans text-xs font-semibold cursor-pointer"
            >
              <i className="fa-solid fa-trash-can text-[10px]" />
              Hapus Semua
            </button>
          </div>

          {completedSections.size === SECTION_KEYS.length && (
            <div className="mt-8 p-6 rounded-2xl border border-white/15 bg-white/[0.04] text-center">
              <p className="text-white/60 font-sans text-sm mb-4">
                Semua bagian telah diisi. Tinjau kembali jawaban Anda sebelum
                mengirim pendaftaran.
              </p>
              <button
                type="button"
                onClick={openReview}
                disabled={submitting}
                className="flex items-center gap-2 mx-auto px-10 py-3.5 rounded-xl bg-white text-midnight
                  font-sans text-sm font-bold transition-all duration-300 hover:scale-105
                  hover:shadow-lg disabled:opacity-60 disabled:hover:scale-100 disabled:cursor-wait"
              >
                <i className="fa-solid fa-clipboard-check text-xs" />
                Tinjau & Daftar
              </button>
              {errors.__submit__ && (
                <p className="mt-3 text-[#EC1B23] font-sans text-xs font-medium">
                  {errors.__submit__}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Dialogs ─────────────────────────────────────────────────────────── */}

      {/* Edit branching answer */}
      <ConfirmationDialog
        open={confirmDialog.open}
        onConfirm={handleConfirmEdit}
        onCancel={handleCancelEdit}
      />

      {/* Reset form */}
      <ConfirmationDialog
        open={resetDialog}
        onConfirm={() => {
          setResetDialog(false);
          resetForm();
        }}
        onCancel={() => setResetDialog(false)}
        title="Hapus Semua Data?"
        message="Seluruh jawaban yang telah Anda isi, termasuk file yang diunggah, akan dihapus dan tidak dapat dikembalikan. Formulir akan kembali ke kondisi awal setelah halaman dimuat ulang.\n\nApakah Anda ingin melanjutkan?"
        confirmLabel="Ya, Hapus"
        confirmStyle="red"
      />

      {/* Final review before submission */}
      <ReviewModal
        open={reviewOpen}
        sections={reviewSections}
        formData={formData}
        primaryContact={primaryContact}
        submitting={submitting}
        error={errors.__submit__}
        onCancel={() => setReviewOpen(false)}
        onConfirm={handleConfirmSubmit}
      />
    </div>
  );
}
